import {Checkbox} from '@wandb/components';
// @todo: Migrate to 'TagGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tag} from '@wandb/weave/components/Tag';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {memo, useEffect, useRef} from 'react';

import {useSelectedCounts} from '../../state/graphql/selectedCountQuery';
import * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import * as ViewHooks from '../../state/views/hooks';
import * as RunSetTypes from '../../state/views/runSet/types';
import * as TempSelectionsActions from '../../state/views/tempSelections/actions';
import * as TempSelectionsTypes from '../../state/views/tempSelections/types';
import {RunWithSupportedKeys} from '../../util/runs';
import * as SM from '../../util/selectionmanager';
import {RunSelectAll} from '../RunSelectAll';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {
  useRunSelectorFeatureDisabled,
  useRunSelectorFeatureEnabled,
} from './RunSelectorFeaturesContext';

export interface RunsTableHeaderProps {
  totalRowCount?: number;
  displayedRows: RunWithSupportedKeys[];
  runSetRef: RunSetTypes.Ref;
  tempSelectionsRef: TempSelectionsTypes.Ref;
  customRunNamesRef?: CustomRunNamesTypes.Ref;
  showTempSelection: boolean;
  onlyShowSelected?: boolean;
  isSingleMode?: boolean;
  onAllItemsTempSelected(): void;
  onAllItemsTempUnselected(): void;
  onVisibilitySelectAll(): void;
  onVisibilitySelectNone(): void;
  onSetOnlyShowSelected(newSetting: boolean): void;
  onSelectVisibleRuns(): void;
}

const RunsTableHeaderComp = (props: RunsTableHeaderProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const prevRowCountRef = useRef(props.totalRowCount);
  useEffect(() => {
    prevRowCountRef.current = props.totalRowCount;
  });
  const selectedCounts = useSelectedCounts(
    props.runSetRef,
    props.tempSelectionsRef,
    props.customRunNamesRef,
    prevRowCountRef.current !== props.totalRowCount
  );

  const runSetPart = ViewHooks.usePart(props.runSetRef);
  const tempSelectNone = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectNone,
    props.tempSelectionsRef,
    runSetPart.groupSelectionsRef
  );
  const tempSelectSome = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectSome,
    props.tempSelectionsRef,
    runSetPart.groupSelectionsRef,
    props.displayedRows
  );
  const tempSelectAllVisible = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectAllVisible,
    props.tempSelectionsRef,
    runSetPart.groupSelectionsRef
  );

  const groupSelections = ViewHooks.usePart(runSetPart.groupSelectionsRef);
  const tempSelections = ViewHooks.usePart(props.tempSelectionsRef);
  const groupTempSelections = {
    grouping: groupSelections.grouping,
    selections: tempSelections,
    expandedRowAddresses: groupSelections.expandedRowAddresses,
  };
  const rootTempCheckedState = SM.getCheckedState(
    groupTempSelections,
    {} as RunWithSupportedKeys,
    0
  );

  const isEnabled = useRunSelectorFeatureEnabled();
  const isDisabled = useRunSelectorFeatureDisabled();
  const isCheckboxDisabled =
    isDisabled('tag') && isDisabled('move') && isDisabled('delete');

  const selectedCountText =
    selectedCounts.tempSelectedCount > 0
      ? `${selectedCounts.tempSelectedCount.toLocaleString()} selected`
      : `${selectedCounts.selectedCount.toLocaleString()} visualized`;

  return (
    <TailwindContents>
      <div className="inline-flex items-center gap-2 -ml-1">
        {!isCheckboxDisabled && props.showTempSelection && (
          <Checkbox
            aria-label="Select all runs"
            data-test="select-all-runs-checkbox"
            isSelected={rootTempCheckedState === 'checked'}
            isIndeterminate={rootTempCheckedState === 'partial'}
            onPress={() => {
              if (rootTempCheckedState !== 'unchecked') {
                tempSelectNone();
                props.onAllItemsTempUnselected();
              } else {
                tempSelectSome();
                props.onAllItemsTempSelected();
              }
              window.analytics?.track('Select all runs clicked', {
                location: analyticsLocation,
              });
            }}
          />
        )}
        {!props.isSingleMode && isEnabled('toggle-visibility') && (
          <RunSelectAll
            allCheckedState={SM.getCheckedState(
              groupSelections,
              {} as RunWithSupportedKeys,
              0
            )}
            onSelectAll={props.onVisibilitySelectAll}
            onSelectNone={props.onVisibilitySelectNone}
            onlyShowSelected={props.onlyShowSelected}
            selectAllVisible={tempSelectAllVisible}
            setOnlyShowSelected={props.onSetOnlyShowSelected}
            onSelectVisibleRuns={props.onSelectVisibleRuns}
          />
        )}
        <span className="flex items-center gap-6 ml-4">
          Name
          <span data-test="selected-count" className="normal-case">
            <Tag color="moon" label={selectedCountText} />
          </span>
        </span>
      </div>
    </TailwindContents>
  );
};

export const RunsTableHeader = memo(RunsTableHeaderComp);
